import { Hand } from "lucide-react";
import { useRaisedHands } from "../../hooks/useRaisedHands";
import HeaderParticipants from "./HeaderParticipants";
import MeetingCauseList from "./MeetingCauseList";
import ControlBar from "./ControlBar";
import MeetingMediaSoup from "../mediasoup/Meeting.MediaSoup";
import ViewerButton from "./ViewerButton";
import ViewerList from "./ViewerList";
import { useMeetingStore } from "../../store/meeting.store";
import { useEffect, useMemo, useRef } from "react";
import { socket } from "../mediasoup/socket";
import toast from "react-hot-toast";
import logger from "../../utils/logger";
import api from "../../lib/api";

const RCJMeeting = () => {
  const cnr = useMeetingStore((s) => s.selectedCase?.cnr);
  const actionBy = useMeetingStore((s) => s.userData?._id);
  const selectedCase = useMeetingStore((s) => s.selectedCase);
  const setSelectedCase = useMeetingStore((s) => s.setSelectedCase);
  const userData = useMeetingStore((s) => s.userData);
  const setUserData = useMeetingStore((s) => s.setUserData);
  const courtRoomData = useMeetingStore((s) => s.courtRoomData);
  const setCourtRoomData = useMeetingStore((s) => s.setCourtRoomData);
  const setCauses = useMeetingStore((s) => s.setCauses);
  const setMeetingStarted = useMeetingStore((s) => s.setMeetingStarted);

  const hearingHands = useRaisedHands(courtRoomData?.courtRoomId);
  const lobbyHands = useRaisedHands(
    userData?.roomId ? `${userData.roomNo}_${userData.roomId}` : undefined,
    userData?.role === "Judge", true,
  );
  const raisedHands = useMemo(
    () => [...hearingHands, ...lobbyHands].sort((a, b) => a.raisedAt - b.raisedAt),
    [hearingHands, lobbyHands],
  );
  const notifiedHands = useRef(new Set<string>());

  useEffect(() => {
    notifiedHands.current.clear();
  }, [courtRoomData?.courtRoomId, userData?.roomId, userData?.role]);

  useEffect(() => {
    if (userData?.role !== "Judge") return;
    for (const hand of raisedHands) {
      const key = JSON.stringify([hand.userId, hand.cnr, hand.raisedAt]);
      if (notifiedHands.current.has(key)) continue;
      notifiedHands.current.add(key);
      toast(`${hand.name || "Advocate"} raised their hand${hand.cnr ? ` (CNR: ${hand.cnr})` : ""}`, {
        id: `raised-hand:${key}`,
        icon: <Hand className="h-5 w-5 text-amber-600" />,
        duration: 5000,
      });
    }
  }, [raisedHands, userData?.role]);

  const viewerShow = useMeetingStore((s) => s.viewerShow);
  const mediaJoining = useMeetingStore((s) => s.mediaJoining);
  useEffect(() => {
    socket.on("end-meeting", (data: any) => {
      logger.log("🏁 endMeeting", data);
      if (data) {
        toast.success("The session has been ended.", {
          id: "meeting-ended",
        });

        setTimeout(() => {
          useMeetingStore.getState().setMeetingStarted(false);
        }, 1000);
      }
    });
    return () => {
      socket.off("end-meeting");
    };
  }, [socket]);

  const leaveMeeting = () => {
    if (useMeetingStore.getState().userData.role !== "Judge") return;
    socket.emit("end-meeting", {
      roomId: useMeetingStore.getState().courtRoomData.courtRoomId,
      userId: useMeetingStore.getState().userData._id,
      actionBy,
      cnr,
    });
  };

  useEffect(() => {
    if (userData?.role !== "Judge" || !courtRoomData?.courtRoomId) return;

    let cancelled = false;
    const currentRoomId = courtRoomData.courtRoomId;

    const clearMeetingForAssignmentChange = (nextCourtRoomData?: any) => {
      socket.emit("end-meeting", {
        roomId: currentRoomId,
        userId: userData._id,
        actionBy: userData._id,
        cnr: selectedCase?.cnr,
      });
      setCauses([]);
      setSelectedCase(null);
      setCourtRoomData(
        nextCourtRoomData || {
          courtRoomId: "",
          courtRoomNo: "",
          courtRoomName: "",
          highcourt: "",
          xPoweredBy: "",
        },
      );
      setUserData({
        ...userData,
        roomId: nextCourtRoomData?.courtRoomId || "",
        roomNo: nextCourtRoomData?.courtRoomNo || "",
      });
      setMeetingStarted(false);
      toast.error("Court assignment changed. Please start the session again.", {
        id: "judge-assignment-changed",
      });
    };

    const syncJudgeAssignment = async () => {
      try {
        const res = await api.get("/api/judge/cases", {
          params: { page: 1, limit: 20 },
        });

        if (cancelled) return;

        const nextRoomId = String(res.data?.courtRoomData?.courtRoomId || "");
        const nextCases = res.data?.data || [];

        if (!nextRoomId || nextRoomId !== String(currentRoomId)) {
          clearMeetingForAssignmentChange(res.data?.courtRoomData);
          return;
        }

        setCauses(nextCases);
        if (
          selectedCase &&
          !nextCases.some((item: any) => item?.cnr === selectedCase.cnr)
        ) {
            const check = await api.get("/api/judge/cases", {
      params: { cnr: selectedCase.cnr, page: 1, limit: 1 },
    });
    const stillExists = (check.data?.data || []).some(
      (item: any) => item?.cnr === selectedCase.cnr,
    );
    if (!stillExists) setSelectedCase(null);
        }
      } catch (error) {
        logger.error("Failed to sync judge assignment", error);
      }
    };

    syncJudgeAssignment();
  }, [
    courtRoomData?.courtRoomId,
    selectedCase,
    setCauses,
    setCourtRoomData,
    setMeetingStarted,
    setSelectedCase,
    setUserData,
    userData,
  ]);

  useEffect(() => {
    const handleUnload = () => leaveMeeting();
    window.addEventListener("beforeunload", handleUnload);

    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [leaveMeeting]);
  return (
    <div className="relative">
      {mediaJoining && (
        <div className="absolute inset-0 z-50 flex items-center justify-center h-100vh bg-black text-white">
          <div className="text-center">
            <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-white mx-auto mb-4" />
            <p className="text-lg font-semibold">Joining meeting...</p>
            <p className="text-sm opacity-70">
              Please wait while we connect audio & video
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <HeaderParticipants raisedHands={userData?.role === "Judge" ? raisedHands : []} />
        <ViewerButton />
      </div>

      {/* Main Layout */}
      <div className="flex h-[calc(100vh-100px)] overflow-hidden">
        {/* Left Sidebar —*/}
        <div
          className={`overflow-y-auto ${mediaJoining ? "invisible" : "visible"}`}
        >
          <MeetingCauseList />
        </div>

        {/* Center */}
        <div className="flex flex-col flex-1 relative transition-all duration-300">
          {/* {userData?.role === "Judge" && raisedHands.length > 0 && (
            <div role="status" aria-live="polite" className="mx-2 mt-2 max-h-32 overflow-y-auto rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950">
              <div className="flex items-center gap-2 font-semibold">
                <Hand className="h-5 w-5" /> Raised hands ({raisedHands.length})
              </div>
              <ul className="mt-1 flex flex-wrap gap-2">
                {raisedHands.map((hand) => (
                  <li key={`${hand.userId}:${hand.cnr}`} className="rounded-lg bg-white px-3 py-1 text-sm">
                    <span className="font-medium">{hand.name}</span>
                    {hand.cnr && <span className="ml-2 text-xs text-slate-600">CNR: {hand.cnr}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )} */}
          <div className="flex-1 overflow-hidden p-1">
            <MeetingMediaSoup raisedHands={hearingHands} />
          </div>

          <div className={`flex items-center justify-center px-1 ${mediaJoining ? "invisible" : "visible"}`}>
            <ControlBar />
          </div>
        </div>

        {/* Right Sidebar Animated */}
        <div
          className={`
      transition-all duration-300 ease-in-out overflow-hidden
      ${viewerShow ? "w-36 opacity-100" : "w-0 opacity-0"}
    `}
        >
          <div className="h-full overflow-y-auto bg-white">
            <ViewerList />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCJMeeting;
